import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useContext,
  useState,
} from 'react';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { useRefillEnergyMutation } from '@/api/queries/user';
import { opacityAnimation } from '@/common/config/motion-animations';
import { SendAnalitics } from '@/common/lib/playdeck';
import { timeStringFormat } from '@/common/lib/timer';
import { EnergyLightningAnimation } from '@/components/Animations/EnergyLightning/EnergyLightning';
import { RestoreEnergyLightAnimation } from '@/components/Animations/RestoreEnergyLightAnimation/RestoreEnergyLightAnimation';
import { CoinBalance } from '@/components/common/CoinBalance/CoinBalance';
import { useCountDownEnergyRefill } from '@/components/common/CoolDownEnergyRefill/count-down-energyRefill.hook';
import { H2, H3, StringMain } from '@/components/Typography/Typography';
import { Button, PurpleButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { SquareCloseIcon } from '@/components/ui/Icons';
import { addToBalance, changeEnergy } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import css from './styles.module.scss';

interface RestoreEnergyProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const RestoreEnergy: FC<RestoreEnergyProps> = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const { openModal } = useContext(ModalContext);
  const energy = useAppSelector((state) => state.user.extendedInfo?.energy);
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const [refillEnergy, { isLoading, isSuccess }] = useRefillEnergyMutation();
  const [isAnimationStart, setIsAnimationStart] = useState(false);
  const secondsToAvailable = useCountDownEnergyRefill();
  const dispatch = useAppDispatch();

  const price = 5;

  const closeModal = (): void => {
    setIsAnimationStart(false);
    setIsOpen(false);

    if (isSuccess) {
      dispatch(changeEnergy(1000));
    }
  };

  const buyEnergy = (): void => {
    if ((tgemBalance || 0) < price) {
      openModal({
        renderKind: 'coinDeposit',
        props: { source: 'restore_energy_no_balance' },
      });
    } else {
      refillEnergy().then(() => {
        dispatch(addToBalance({ tgem: price * -1 }));
        SendAnalitics.coinSpend({
          amount: price,
          type: 'roll_energy',
          item_name: 'energy',
        });
        setIsAnimationStart(true);
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && energy !== undefined && (
        <motion.div
          className="fixed top-0 left-0 z-[99]  bg-black/80 backdrop-blur-[8px]"
          style={{ width: window.innerWidth, height: window.innerHeight }}
          {...opacityAnimation}
        >
          <RestoreEnergyLightAnimation isAnimationStart={isAnimationStart} />
          <div className="w-full h-full flex flex-col justify-between items-center pt-[calc(var(--tg-safe-area-inset-top,_0px)_+_80px)] pb-16">
            <CoinBalance />
            <div className="flex flex-col items-center mb-8">
              <H2 weight="bold" className="!text-white mb-8">
                {t('Recharge Energy')}
              </H2>
              <EnergyLightningAnimation
                energy={energy}
                isAnimationStart={isAnimationStart}
                className="mb-4"
              />
              <span className="font-extrabold text-[2.5rem] text-white leading-none mb-6">
                <CountUp
                  separator=""
                  start={energy}
                  end={isAnimationStart ? 1000 : energy}
                  useEasing={false}
                  duration={1}
                />
              </span>
              <div className="w-[10.5rem] h-12">
                {!isAnimationStart && (
                  <PurpleButton
                    onClick={buyEnergy}
                    inactive={!!secondsToAvailable}
                    disabled={isLoading || !!secondsToAvailable}
                    isLoading={isLoading}
                    className="w-[10.5rem] flex items-center gap-1"
                  >
                    {!secondsToAvailable ? (
                      <>
                        <CurrencyImage size={'1.5rem'} kind="tgem" />
                        <H3
                          weight="extrabold"
                          className={cn('text-white/80', css.btnText)}
                        >
                          {price}
                        </H3>
                      </>
                    ) : (
                      <StringMain
                        weight="extrabold"
                        className="!backdrop-filter-none !bg-transparent"
                      >{`${t('Next in')} ${timeStringFormat(secondsToAvailable * 1000)}`}</StringMain>
                    )}
                  </PurpleButton>
                )}
              </div>
            </div>
            <Button
              onClick={closeModal}
              variant="transparent"
              className="!w-auto"
            >
              <SquareCloseIcon className="w-16 h-16 shrink-0" />
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { RestoreEnergy };
